/**
 * Secrets Collective — "Complete the set" cross-sell.
 *
 * Reads the main product's current selection straight out of the PDP (the
 * hidden variantId input and the pressed colour swatch), so the partner piece
 * always matches the colour on screen without the customer choosing twice.
 *
 * Adds both pieces as separate line items. The saving itself comes from an
 * automatic discount in admin, not from this file — the price shown here is a
 * preview of what that discount will do.
 */

import { addItems } from './sc-bundle.js';

class SCCompleteSet extends HTMLElement {
  connectedCallback() {
    this.variants = this.#json('partnerVariants');
    this.sizeIndex = Number(this.dataset.sizeIndex) || 0;
    this.colourIndex = Number(this.dataset.colourIndex) || 0;
    this.matchColour = this.dataset.matchColour === 'true';
    this.discount = Number(this.dataset.discountCents) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '';

    this.cta = this.querySelector('[ref="cta"]');
    this.ctaText = this.querySelector('[ref="ctaText"]');
    this.msg = this.querySelector('[ref="msg"]');
    this.colourName = this.querySelector('[ref="colourName"]');
    this.wasPrice = this.querySelector('[ref="wasPrice"]');
    this.nowPrice = this.querySelector('[ref="nowPrice"]');
    this.savingLabel = this.querySelector('[ref="savingLabel"]');

    this.pdp = this.closest('sc-product');
    this.selectedSize = null;

    this.querySelectorAll('[data-cts-size]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.querySelectorAll('[data-cts-size]').forEach((o) =>
          o.setAttribute('aria-pressed', 'false')
        );
        btn.setAttribute('aria-pressed', 'true');
        this.selectedSize = btn.dataset.value;
        this.#update();
      });
    });

    // Re-evaluate whenever the main product's options change. Those buttons
    // carry no data-set, which is what separates them from the bundle's.
    this.pdp?.addEventListener('click', (e) => {
      if (e.target.closest('[data-option-index]:not([data-set])')) {
        // Let the PDP's own handler run first.
        requestAnimationFrame(() => this.#update());
      }
    });

    this.cta?.addEventListener('click', () => this.#submit());
    this.#update();
  }

  #json(ref) {
    const el = this.querySelector(`[ref="${ref}"]`);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch {
      return [];
    }
  }

  /** The colour currently selected on the main product, if any. */
  #mainColour() {
    if (!this.matchColour || !this.pdp) return null;
    const pressed = this.pdp.querySelectorAll(
      '.sc-pdp__opt-values--swatch [data-option-index]:not([data-set])[aria-pressed="true"]'
    );
    return pressed.length ? pressed[0].dataset.value : null;
  }

  /** Resolve the partner variant from the matched colour + chosen size. */
  #partnerVariant() {
    if (!this.selectedSize) return null;
    const colour = this.#mainColour();
    return (
      this.variants.find((v) => {
        const sizeOk = v.options[this.sizeIndex - 1] === this.selectedSize;
        if (!sizeOk) return false;
        if (!colour || !this.colourIndex) return true;
        return v.options[this.colourIndex - 1] === colour;
      }) || null
    );
  }

  #mainVariantId() {
    return this.pdp?.querySelector('[ref="variantId"]')?.value || null;
  }

  #update() {
    const colour = this.#mainColour();
    if (this.colourName) this.colourName.textContent = colour || '';

    // Grey out partner sizes that don't exist in the matched colour.
    this.querySelectorAll('[data-cts-size]').forEach((btn) => {
      const exists = this.variants.some(
        (v) =>
          v.available &&
          v.options[this.sizeIndex - 1] === btn.dataset.value &&
          (!colour || !this.colourIndex || v.options[this.colourIndex - 1] === colour)
      );
      btn.classList.toggle('is-unavailable', !exists);
      btn.disabled = !exists;
      if (!exists && btn.getAttribute('aria-pressed') === 'true') {
        btn.setAttribute('aria-pressed', 'false');
        this.selectedSize = null;
      }
    });

    const partner = this.#partnerVariant();
    const fmt = (c) => money(c, this.moneyFormat);

    if (this.savingLabel) {
      this.savingLabel.textContent = this.discount > 0 ? `Save ${fmt(this.discount)}` : '';
    }

    if (partner) {
      const discounted = Math.max(0, partner.price - this.discount);
      if (this.wasPrice) this.wasPrice.textContent = fmt(partner.price);
      if (this.nowPrice) this.nowPrice.textContent = fmt(discounted);
    } else {
      const base = this.variants[0];
      if (this.wasPrice && base) this.wasPrice.textContent = fmt(base.price);
      if (this.nowPrice && base) this.nowPrice.textContent = fmt(Math.max(0, base.price - this.discount));
    }

    const ready = Boolean(partner && this.#mainVariantId());
    if (this.cta) this.cta.disabled = !ready;
    if (this.ctaText) {
      if (!ready) {
        this.ctaText.textContent = this.dataset.labelChoose || 'Select a size';
      } else {
        const mainPrice = this.#mainPriceCents();
        const total = mainPrice + Math.max(0, partner.price - this.discount);
        const label = this.dataset.labelAdd || 'Add both';
        this.ctaText.textContent = mainPrice ? `${label} — ${fmt(total)}` : label;
      }
    }
  }

  /** Main product price in cents, read from the rendered price element. */
  #mainPriceCents() {
    const text = this.pdp?.querySelector('[ref="price"]')?.textContent || '';
    const n = parseFloat(text.replace(/[^0-9.,]/g, '').replace(/,/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  async #submit() {
    const partner = this.#partnerVariant();
    const mainId = this.#mainVariantId();
    if (!partner || !mainId || this.cta.disabled) return;

    this.cta.disabled = true;
    const original = this.ctaText.textContent;
    this.ctaText.textContent = 'Adding…';
    this.msg?.classList.remove('is-error');

    const result = await addItems(this, [
      { id: mainId, quantity: 1 },
      { id: partner.id, quantity: 1 },
    ]);

    if (result.ok) {
      this.ctaText.textContent = 'Added';
      if (this.msg) this.msg.textContent = this.dataset.success || '';
      setTimeout(() => {
        this.cta.disabled = false;
        this.#update();
      }, 1600);
    } else {
      this.cta.disabled = false;
      this.ctaText.textContent = original;
      if (this.msg) {
        this.msg.textContent = result.message || 'Could not add to cart';
        this.msg.classList.add('is-error');
      }
    }
  }
}

function money(cents, format) {
  const amount = cents / 100;
  const plain = amount.toFixed(2);
  const withCommas = plain.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (typeof format === 'string' && format.includes('{{')) {
    return format.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_m, token) =>
      token === 'amount_no_decimals' ? Math.round(amount).toLocaleString('en') : withCommas
    );
  }
  return `$${withCommas}`;
}

if (!customElements.get('sc-complete-set')) {
  customElements.define('sc-complete-set', SCCompleteSet);
}
